import { useEffect, useMemo, useState } from 'react';
import { CornerDownLeft } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { PromptBodyPreview } from 'pages/prompts/components/prompt-body-preview';
import type { PromptItem } from 'pages/prompts/types';
import {
  applyPromptVariables,
  extractVariables,
  formatVariableLabel,
} from 'pages/prompts/utils';

type ApplyPromptVariablesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: PromptItem | null;
  onApply: (resolvedBody: string) => void;
};

export function ApplyPromptVariablesModal({
  open,
  onOpenChange,
  prompt,
  onApply,
}: ApplyPromptVariablesModalProps) {
  const variables = useMemo(
    () => (prompt ? extractVariables(prompt.body) : []),
    [prompt]
  );

  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !prompt) return;
    const initial: Record<string, string> = {};
    for (const variable of extractVariables(prompt.body)) {
      initial[variable] = '';
    }
    setValues(initial);
  }, [open, prompt]);

  const handleApply = () => {
    if (!prompt) return;
    onApply(applyPromptVariables(prompt.body, values));
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setValues({});
    }
  };

  if (!prompt) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        overlayClassName="z-[110]"
        className="z-[110] flex max-h-[min(90vh,100dvh-2rem)] w-[calc(100vw-2rem)] max-w-lg flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="shrink-0 space-y-0 border-b border-border/60 px-4 py-4 sm:px-5">
          <DialogTitle className="pr-8 text-left text-lg font-semibold">{prompt.title}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-5">
          <div className="min-w-0 rounded-lg border border-border/60 bg-muted/40 px-3 py-3 sm:px-4">
            <PromptBodyPreview
              body={prompt.body}
              className="break-words text-sm leading-relaxed text-foreground whitespace-pre-wrap"
            />
          </div>

          {variables.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">Variables</p>
              <p className="text-xs text-muted-foreground">
                Optional. Leave blank to use the variable name in the prompt.
              </p>
              <div className="space-y-3">
                {variables.map((variable) => (
                  <div key={variable} className="space-y-1.5">
                    <Label htmlFor={`prompt-var-${variable}`} className="sr-only">
                      {formatVariableLabel(variable)}
                    </Label>
                    <Input
                      id={`prompt-var-${variable}`}
                      value={values[variable] ?? ''}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [variable]: event.target.value,
                        }))
                      }
                      placeholder={formatVariableLabel(variable)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleApply();
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border/60 px-4 py-4 sm:justify-end sm:px-5">
          <Button type="button" onClick={handleApply} className="w-full gap-2 sm:w-auto">
            Apply prompt
            <CornerDownLeft className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
